import React, { useState, useEffect } from 'react';
import { Provider } from '../types';
import { providersAPI } from '../services/api';
import { Star, MapPin, Clock, CheckCircle } from 'lucide-react';

interface ProviderSelectionProps {
  selectedProvider: Provider | null;
  onSelectProvider: (provider: Provider) => void;
  skill?: string;
  category?: string;
}

const ProviderSelection: React.FC<ProviderSelectionProps> = ({
  selectedProvider,
  onSelectProvider,
  skill,
  category
}) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProviders();
  }, [skill, category]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await providersAPI.getProviders({ skill, category });
      setProviders(response.data.providers);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setError('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating})</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        {category === 'driver' && <p className="ml-3 text-gray-600">Getting your location...</p>}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => fetchProviders()}
          className="mt-4 px-4 py-2 bg-black text-white rounded-md hover:bg-black700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No providers available for this service in your area.</p>
        {category === 'driver' && (
          <p className="text-sm text-gray-500 mt-2">Try expanding your search radius</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Select a Service Provider</h3>
      
      <div className="grid gap-4 md:grid-cols-2">
        {providers.map((provider) => (
          <div
            key={provider._id || provider.id}
            className={`relative p-6 bg-white border rounded-lg cursor-pointer transition-all hover:shadow-md ${
              (selectedProvider?._id || selectedProvider?.id) === (provider._id || provider.id)
                ? 'border-black bg-white'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onSelectProvider(provider)}
          >
            {selectedProvider?.id === provider.id && (
              <div className="absolute top-4 right-4">
                <CheckCircle className="h-6 w-6 text-black600" />
              </div>
            )}

            <div className="flex items-start space-x-4">
              {/* Provider Avatar */}
              <div className="flex-shrink-0">
                {provider.avatar ? (
                  <img
                    src={provider.avatar}
                    alt={provider.name}
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="h-12 w-12 sm:h-14 sm:w-14 bg-black100 rounded-full flex items-center justify-center border-2 border-gray-200">
                    <span className="text-black600 font-semibold text-lg sm:text-xl">
                      {provider.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-gray-900 truncate">
                    {provider.name}
                  </h4>
                  {provider.isVerified && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>

                <div className="mt-1">
                  {renderStars(provider.rating)}
                </div>

                <div className="mt-2 flex items-center text-sm text-gray-600">
                  <span>₹{typeof provider.hourlyRate === 'number' ? provider.hourlyRate.toFixed(2) : provider.hourlyRate}/hour</span>
                </div>

                <div className="mt-2 flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{provider.reviewCount} reviews</span>
                </div>

                {provider.skills && provider.skills.length > 0 && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-1">
                      {provider.skills.slice(0, 3).map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {skill}
                        </span>
                      ))}
                      {provider.skills.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          +{provider.skills.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className={`h-2 w-2 rounded-full mr-2 ${
                        provider.isAvailable ? 'bg-green-400' : 'bg-red-400'
                      }`}
                    />
                    <span className={`text-sm ${
                      provider.isAvailable ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {provider.isAvailable ? 'Available' : 'Busy'}
                    </span>
                  </div>
                  
                  {provider.distance !== undefined && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{typeof provider.distance === 'number' ? provider.distance.toFixed(2) : provider.distance || '0.00'} km away</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProviderSelection; 